// Génération d'une carte story 1080×1920 (classement) à partager (Instagram, WhatsApp…)
const W = 1080;
const H = 1920;

const MEDALS = ["#D4AF37", "#C7C9CE", "#C08A50"];

function hexA(hex, a) {
  const h = (hex || "#D4AF37").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

function loadImage(url) {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function ellipsize(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 2 && ctx.measureText(t + "…").width > maxWidth) t = t.slice(0, -1);
  return t + "…";
}

function wrapTitle(ctx, text, maxWidth, maxLines = 3) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = ellipsize(ctx, kept[maxLines - 1] + "…", maxWidth);
    return kept;
  }
  return lines;
}

function drawLogoCircle(ctx, img, x, y, r, color, name) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = img ? "#FFFFFF" : hexA(color || "#444444", 0.25);
  ctx.fill();
  ctx.clip();
  if (img) {
    const pad = r * 0.16;
    const size = (r - pad) * 2;
    const ratio = Math.min(size / img.width, size / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    ctx.drawImage(img, x - dw / 2, y - dh / 2, dw, dh);
  } else {
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `800 ${Math.round(r)}px 'Barlow Condensed', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((name || "?").charAt(0).toUpperCase(), x, y + 2);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 3;
  ctx.stroke();
}

/**
 * rows: [{ position, name, logo, color, value }] — value optionnel (ex. points)
 */
export async function generateRankingCard({
  title,
  subtitle = "",
  kicker = "CLASSEMENT FINAL",
  accent = "#D4AF37",
  rows = [],
  footer = "",
}) {
  try {
    await document.fonts.ready;
  } catch (e) {
    /* police par défaut */
  }
  const shown = rows.slice(0, 10);
  const logos = await Promise.all(shown.map((r) => loadImage(r.logo)));

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // fond + halo accent
  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 80, 0, W / 2, 80, 1150);
  glow.addColorStop(0, hexA(accent, 0.26));
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, 1250);
  const glow2 = ctx.createRadialGradient(W / 2, H, 0, W / 2, H, 700);
  glow2.addColorStop(0, hexA(accent, 0.12));
  glow2.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow2;
  ctx.fillRect(0, H - 760, W, 760);

  // cadre
  ctx.strokeStyle = hexA(accent, 0.6);
  ctx.lineWidth = 5;
  ctx.strokeRect(42, 42, W - 84, H - 84);
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 2;
  ctx.strokeRect(58, 58, W - 116, H - 116);

  ctx.textAlign = "center";
  try {
    ctx.letterSpacing = "7px";
  } catch (e) {
    /* non supporté */
  }
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "800 30px Manrope, sans-serif";
  ctx.fillText("LASER TECH CRÉATION", W / 2, 150);

  ctx.fillStyle = accent;
  ctx.font = "800 38px Manrope, sans-serif";
  ctx.fillText(kicker.toUpperCase(), W / 2, 236);
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {
    /* non supporté */
  }

  // titre
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 92px 'Clash Display', 'Barlow Condensed', sans-serif";
  const lines = wrapTitle(ctx, (title || "").toUpperCase(), W - 200);
  let y = 350;
  lines.forEach((l) => {
    ctx.fillText(l, W / 2, y);
    y += 100;
  });
  if (subtitle) {
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "600 34px Manrope, sans-serif";
    ctx.fillText(ellipsize(ctx, subtitle, W - 200), W / 2, y + 8);
    y += 60;
  }

  // séparateur
  y += 30;
  ctx.strokeStyle = hexA(accent, 0.5);
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 120, y);
  ctx.lineTo(W / 2 + 120, y);
  ctx.stroke();
  y += 40;

  // lignes de classement
  const bottom = H - 190;
  const available = bottom - y;
  const podiumH = Math.min(150, available / (shown.length > 3 ? 2.6 : 1) / 3);
  const restCount = Math.max(shown.length - 3, 0);
  const restH = restCount ? Math.min(96, (available - podiumH * Math.min(shown.length, 3) - 30) / restCount) : 0;

  shown.forEach((r, i) => {
    const isPodium = i < 3;
    const rowH = isPodium ? podiumH : restH;
    const cy = y + rowH / 2;
    const leftX = 120;
    // position
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = isPodium ? MEDALS[i] : "rgba(255,255,255,0.4)";
    ctx.font = `800 ${isPodium ? 64 : 44}px 'Barlow Condensed', sans-serif`;
    const posTxt = `${r.position}${r.position === 1 ? "ᵉʳ" : "ᵉ"}`;
    ctx.fillText(posTxt, leftX, cy);
    // logo
    const logoR = isPodium ? 46 : 30;
    const logoX = leftX + 140 + logoR;
    drawLogoCircle(ctx, logos[i], logoX, cy, logoR, r.color, r.name);
    // nom
    ctx.fillStyle = isPodium ? "#FFFFFF" : "rgba(255,255,255,0.85)";
    ctx.font = `700 ${isPodium ? 52 : 38}px 'Barlow Condensed', sans-serif`;
    const nameX = logoX + logoR + 28;
    const nameMax = W - nameX - (r.value ? 220 : 130);
    ctx.fillText(ellipsize(ctx, (r.name || "").toUpperCase(), nameMax), nameX, cy);
    // valeur (points)
    if (r.value !== undefined && r.value !== null && r.value !== "") {
      ctx.textAlign = "right";
      ctx.fillStyle = accent;
      ctx.font = `800 ${isPodium ? 52 : 38}px 'Barlow Condensed', sans-serif`;
      ctx.fillText(String(r.value), W - 120, cy);
      ctx.textAlign = "left";
    }
    // ligne
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(110, y + rowH);
    ctx.lineTo(W - 110, y + rowH);
    ctx.stroke();
    y += rowH + (i === 2 && restCount ? 26 : 0);
  });

  // pied de page
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = "600 28px Manrope, sans-serif";
  if (footer) ctx.fillText(ellipsize(ctx, footer, W - 200), W / 2, H - 118);
  ctx.fillStyle = hexA(accent, 0.8);
  ctx.font = "800 26px Manrope, sans-serif";
  ctx.fillText("PAGE CONNECTÉE · QR VÉRIFIÉ", W / 2, H - 76);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

/**
 * Carte joueur : grande photo + stats clés.
 * { name, subtitle, photo, accent, stats: [{label, value}], footer }
 */
export async function generatePlayerCard({
  name,
  subtitle = "",
  photo = "",
  accent = "#D4AF37",
  stats = [],
  footer = "",
}) {
  try {
    await document.fonts.ready;
  } catch (e) {
    /* police par défaut */
  }
  const img = await loadImage(photo);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);

  // photo pleine largeur (cover) sur ~62% de la hauteur
  const photoH = Math.round(H * 0.62);
  if (img) {
    const ratio = Math.max(W / img.width, photoH / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    ctx.drawImage(img, (W - dw) / 2, (photoH - dh) / 2, dw, dh);
  } else {
    const ph = ctx.createLinearGradient(0, 0, 0, photoH);
    ph.addColorStop(0, hexA(accent, 0.3));
    ph.addColorStop(1, "#0A0B0E");
    ctx.fillStyle = ph;
    ctx.fillRect(0, 0, W, photoH);
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.font = "800 420px 'Barlow Condensed', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((name || "?").charAt(0).toUpperCase(), W / 2, photoH / 2);
    ctx.textBaseline = "alphabetic";
  }
  // fondu photo -> fond
  const fade = ctx.createLinearGradient(0, photoH - 520, 0, photoH + 10);
  fade.addColorStop(0, "rgba(5,5,5,0)");
  fade.addColorStop(1, "#050505");
  ctx.fillStyle = fade;
  ctx.fillRect(0, photoH - 520, W, 540);

  // cadre
  ctx.strokeStyle = hexA(accent, 0.6);
  ctx.lineWidth = 5;
  ctx.strokeRect(42, 42, W - 84, H - 84);

  // bandeau haut
  ctx.textAlign = "center";
  try {
    ctx.letterSpacing = "7px";
  } catch (e) {
    /* non supporté */
  }
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "800 30px Manrope, sans-serif";
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.8)";
  ctx.shadowBlur = 14;
  ctx.fillText("LASER TECH CRÉATION", W / 2, 140);
  ctx.restore();
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {
    /* non supporté */
  }

  // nom + sous-titre
  let y = photoH - 60;
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 100px 'Clash Display', 'Barlow Condensed', sans-serif";
  const lines = wrapTitle(ctx, (name || "").toUpperCase(), W - 180, 2);
  lines.forEach((l) => {
    ctx.fillText(l, W / 2, y);
    y += 108;
  });
  if (subtitle) {
    ctx.fillStyle = accent;
    ctx.font = "800 38px Manrope, sans-serif";
    ctx.fillText(ellipsize(ctx, subtitle.toUpperCase(), W - 200), W / 2, y + 4);
    y += 70;
  }

  // stats (jusqu'à 4 cases)
  const shown = stats.slice(0, 4);
  if (shown.length) {
    const gap = 24;
    const boxW = (W - 160 - gap * (shown.length - 1)) / shown.length;
    const boxH = 220;
    const by = Math.min(y + 40, H - 190 - boxH - 40);
    shown.forEach((s, i) => {
      const bx = 80 + i * (boxW + gap);
      ctx.fillStyle = "rgba(255,255,255,0.045)";
      ctx.strokeStyle = hexA(accent, 0.35);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(bx, by, boxW, boxH, 18);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = accent;
      ctx.font = "800 86px 'Barlow Condensed', sans-serif";
      ctx.fillText(String(s.value ?? 0), bx + boxW / 2, by + 112);
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = "700 26px Manrope, sans-serif";
      ctx.fillText(ellipsize(ctx, (s.label || "").toUpperCase(), boxW - 24), bx + boxW / 2, by + 170);
    });
  }

  // pied de page
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = "600 28px Manrope, sans-serif";
  if (footer) ctx.fillText(ellipsize(ctx, footer, W - 200), W / 2, H - 118);
  ctx.fillStyle = hexA(accent, 0.8);
  ctx.font = "800 26px Manrope, sans-serif";
  ctx.fillText("PAGE CONNECTÉE · QR VÉRIFIÉ", W / 2, H - 76);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export function generateCard(card) {
  return card.type === "player" ? generatePlayerCard(card) : generateRankingCard(card);
}
